import { useEffect, useState, useContext, useCallback, useMemo } from 'react'
import styles from './styles.module.css'
import { useParams } from 'react-router-dom'
import { fetchData } from '../../../../../global/utils/fetch'
import { useAuthenticator } from '@aws-amplify/ui-react'
import { ToastNotificationContext } from '../../../../../global/context/toast-context/ToastNotificationContext'
import getUsername from '../../../../../global/utils/getUsername'
import AddNoteInput from './AddNoteInput'
import { getOrderNotes, createOrderNote } from './api'
import { NoteType } from '../../../../../../../app/types/order-notes'
import { IOrderNote } from '../../../../../../../app/entities/OrderNote'
import OrderNotesTable, {
  OnsiteCancellationNote,
} from '../../../../../global/components/order-notes-table/OrderNotesTable'
import { WoDetailContext } from '../../../context/context'

interface IWorkOrderNotesProps {
  isReadOnly: boolean
}

export default function WorkOrderNotes({ isReadOnly }: IWorkOrderNotesProps) {
  const { id: orderObjectId } = useParams()
  const [notes, setNotes] = useState<IOrderNote[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const { user } = useAuthenticator((context) => [context.user])
  const username = getUsername(user)
  const { data: orderData } = useContext(WoDetailContext)

  // Create a virtual legacy note from order.note field if it exists
  // This displays the deprecated order.note field as a legacy_note in the table
  // for orders that weren't migrated to the orderNotes entity
  const legacyNoteFromOrder = useMemo((): IOrderNote | null => {
    const orderNote = orderData?.note
    if (!orderNote || orderNote.trim() === '') return null

    // Check if there's already a legacy_note with this exact content to avoid duplicates
    // (orders created in last 2 months had their notes migrated)
    const alreadyMigrated = notes.some(
      (n) => n.type === 'legacy_note' && n.content === orderNote,
    )
    if (alreadyMigrated) return null

    return {
      objectId: `legacy-${orderData.objectId}`,
      type: 'legacy_note',
      content: orderNote,
      createdBy: null,
      created: null as unknown as number, // No created date for legacy notes
      updated: null as unknown as number,
    }
  }, [orderData?.note, orderData?.objectId, notes])

  // Extract on-site cancellation data from order vehicles
  const onsiteCancellation = useMemo((): OnsiteCancellationNote | null => {
    const orderVehicles = orderData?.orderVehicles || []
    // Find the first order vehicle that has on-site cancellation data
    const vehicleWithCos = orderVehicles.find(
      (ov) => ov.cosReason1 || ov.cosNote || ov.cosReason2 || ov.cosReason3,
    )

    if (!vehicleWithCos) return null

    return {
      cosReason1: vehicleWithCos.cosReason1,
      cosReason2: vehicleWithCos.cosReason2,
      cosReason3: vehicleWithCos.cosReason3,
      cosNote: vehicleWithCos.cosNote,
    }
  }, [orderData?.orderVehicles])

  const loadNotes = useCallback(async () => {
    if (!orderObjectId) return
    setIsLoading(true)
    try {
      const fetchedNotes = await fetchData<IOrderNote[]>(
        getOrderNotes(orderObjectId),
      )
      setNotes(fetchedNotes || [])
    } catch (err) {
      console.error('Error loading notes:', err)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        severity: 'error',
        message: 'Error loading notes!',
      })
    } finally {
      setIsLoading(false)
    }
  }, [orderObjectId])

  useEffect(() => {
    loadNotes()
  }, [loadNotes])

  async function handleAddNote(type: NoteType, content: string) {
    if (!orderObjectId || !username) return

    setIsSubmitting(true)
    try {
      await fetchData<IOrderNote>(
        createOrderNote(orderObjectId, type, content, username),
      )
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        severity: 'success',
        message: 'Note added successfully!',
      })
      await loadNotes()
    } catch (err) {
      console.error('Error adding note:', err)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        severity: 'error',
        message: 'Error adding note!',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Combine fetched notes with the legacy note from order.note field
  const allNotes = useMemo(() => {
    if (legacyNoteFromOrder) {
      return [...notes, legacyNoteFromOrder]
    }
    return notes
  }, [notes, legacyNoteFromOrder])

  return (
    <div className={styles.container}>
      <OrderNotesTable
        notes={allNotes}
        isLoading={isLoading}
        height={200}
        title='Work Order Notes'
        onsiteCancellation={onsiteCancellation}
      />

      {!isReadOnly && (
        <AddNoteInput
          onSubmit={handleAddNote}
          isReadOnly={isReadOnly}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  )
}
